import type { DraftPlayerResponse } from '@on3/api';
import clsx from 'clsx';
import { Filter, makeList } from 'components/Db/Filters/Filter';
import { TextLink } from 'components/UI/Link/TextLink';
import { Text } from 'components/UI/Typography/Text';
import { useRouter } from 'next/router';

import { IndustryComparisonDraftList } from '../Rankings/IndustryComparison/IndustryComparisonDraftList';
import { DraftItem } from './DraftItem';
import { DraftListHeader } from './DraftListHeader';
import styles from './DraftPage.module.scss';

export interface IDraftPageProps {
  draftList: DraftPlayerResponse[];
  years: number[];
}

const rounds = [
  { key: 0, name: '1' },
  { key: 1, name: '2' },
  { key: 2, name: '3' },
  { key: 3, name: '4' },
  { key: 4, name: '5' },
  { key: 5, name: '6' },
  { key: 6, name: '7' },
];

export const DraftPage = ({ draftList, years }: IDraftPageProps) => {
  const router = useRouter();
  const {
    year,
    round,
    'industry-comparison': industryComparison,
  } = router.query;
  const isNBA = router.pathname.includes('nba');
  const displayedRounds = isNBA ? rounds.slice(0, 2) : rounds;
  const association = isNBA ? 'NBA' : 'NFL';
  const sortedDraftList = draftList?.sort((a, b) => {
    if (a?.pick && b?.pick) {
      return a?.pick < b?.pick ? -1 : 1;
    }

    return 0;
  });

  return (
    <section>
      <header className={styles.draftHeader}>
        <Text className={styles.title} variant="h1">
          {year} {association} Draft
        </Text>
      </header>
      <div className={styles.filterLinksContainer}>
        <div className={styles.filterBlock}>
          <Filter
            list={makeList(
              years.filter((yearItem) => yearItem <= new Date().getFullYear()),
            )}
            name="year"
            selected={`${year}`}
          />
          <div className={styles.roundBlock}>
            <Text className={styles.roundText} variant="subtitle1">
              round:
            </Text>
            <Filter
              list={displayedRounds}
              name="round"
              selected={`${round || 1}`}
            />
          </div>
        </div>
        <div className={styles.toggleListContainer}>
          <TextLink
            className={clsx(styles.toggleListLink, {
              [styles.active]: !industryComparison,
            })}
            href={`/${isNBA ? 'nba' : 'nfl'}/draft/${year}/${
              round ? `?round=${round}` : ''
            }`}
            underline="none"
            variant="subtitle1"
          >
            college rating
          </TextLink>
          <TextLink
            className={clsx(styles.toggleListLink, {
              [styles.active]: industryComparison,
            })}
            href={`/${
              isNBA ? 'nba' : 'nfl'
            }/draft/${year}/?industry-comparison=true${
              round ? `&round=${round}` : ''
            }`}
            underline="none"
            variant="subtitle1"
          >
            industry comparison
          </TextLink>
        </div>
      </div>
      {!industryComparison && (
        <>
          <DraftListHeader />
          <ol>
            {draftList.length === 0 && <li>No draft picks yet</li>}
            {sortedDraftList.map((draftPick) => {
              return <DraftItem draftPick={draftPick} key={draftPick.key} />;
            })}
          </ol>
        </>
      )}

      {industryComparison && (
        <IndustryComparisonDraftList
          key={`${year}-${round}`}
          playerList={sortedDraftList}
        />
      )}
    </section>
  );
};
